import styled, { keyframes } from 'styled-components'
import { DefaultTheme } from 'styled-components'
import { ColorSchemeTypes } from '../../../types/ColorScheme'
import { Heading, Paragraph } from '../../atoms/Typography/Typography.styles'
import { ResourceHubElementWithColorSchemeProps } from '../../../models/organisms/ResourceHub'
import { StyledButton } from '../../atoms/Button/Button.styles'
import { Icon } from '../../atoms/Icon/Icon.styles'
import { SearchInputButton } from '../../molecules/SearchInput/SearchInput.styles'

interface ResourceHubProps {
  theme: DefaultTheme
  colorScheme: ColorSchemeTypes
}

export const ResourceHub = styled.section<ResourceHubProps>`
  position: relative;
  width: 100%;

  &[data-view-state="results"] {
    min-height: ${({ theme }) => theme.rem(400)};
  }

  &[data-view-state="landing"] {
    min-height: 70vh;
    display: flex;
    align-items: center;
    justify-content: center;

    ${({ theme }) => theme.mediaQuery.lg(`
      min-height: unset;
      display: block;
    `)}
  }
`

export const ResourceHubLandingWrapper = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;

  ${({ theme }) => theme.mediaQuery.lg(`
    padding-top: ${theme.rem(100)};
    padding-bottom: ${theme.rem(224)};
  `)}
`

export const LandingImagesGroup = styled.div`
  display: none;
  position: absolute;

  ${({ theme }) => theme.mediaQuery.lg(`
    display: block;
  `)}
`

export const LandingImageWrapper = styled.div`
  position: absolute;
  border-radius: ${({ theme }) => theme.rem(8)};
  overflow: hidden;

  img {
    object-fit: cover;
    object-position: center;
  }
`

export const TopLeftImages = styled(LandingImagesGroup)`
  top: 0;
  left: 0;
  width: ${({ theme }) => theme.rem(320)};
  height: ${({ theme }) => theme.rem(300)};

  ${LandingImageWrapper}:nth-child(1) {
    top: 0;
    left: ${({ theme }) => theme.rem(96)};
    width: ${({ theme }) => theme.rem(268)};
    height: ${({ theme }) => theme.rem(200)};
  }

  ${LandingImageWrapper}:nth-child(2) {
    top: ${({ theme }) => theme.rem(112)};
    left: ${({ theme }) => theme.rem(-96)};
    width: ${({ theme }) => theme.rem(485)};
    height: ${({ theme }) => theme.rem(274)};
    border-radius: 0;
    mask-image: url('/masks/mask-ellipse.svg');
    mask-size: 100% 100%;
    mask-repeat: no-repeat;
    mask-position: center;
  }
`

export const TopRightImage = styled(LandingImagesGroup)`
  top: 0;
  right: 0;
  width: ${({ theme }) => theme.rem(200)};
  height: ${({ theme }) => theme.rem(200)};

  ${LandingImageWrapper} {
    top: ${({ theme }) => theme.rem(48)};
    right: 0;
    width: 100%;
    height: 100%;
  }
`

export const BottomLeftImage = styled(LandingImagesGroup)`
  bottom: 0;
  left: 0;
  width: ${({ theme }) => theme.rem(228)};
  height: ${({ theme }) => theme.rem(170)};

  ${LandingImageWrapper} {
    top: ${({ theme }) => theme.rem(-32)};
    left: ${({ theme }) => theme.rem(50)};
    width: 100%;
    height: 100%;
  }
`

export const BottomRightImages = styled(LandingImagesGroup)`
  bottom: 0;
  right: 0;
  width: ${({ theme }) => theme.rem(320)};
  height: ${({ theme }) => theme.rem(280)};

  ${LandingImageWrapper}:nth-child(1) {
    top: ${({ theme }) => theme.rem(64)};
    right: 0;
    width: ${({ theme }) => theme.rem(228)};
    height: ${({ theme }) => theme.rem(170)};
    z-index: 1;
  }

  ${LandingImageWrapper}:nth-child(2) {
    top: ${({ theme }) => theme.rem(60)};
    right: ${({ theme }) => theme.rem(48)};
    width: ${({ theme }) => theme.rem(485)};
    height: ${({ theme }) => theme.rem(274)};
    border-radius: 0;
    mask-image: url('/masks/mask-ellipse.svg');
    mask-size: 100% 100%;
    mask-repeat: no-repeat;
    mask-position: center;
  }
`

export const ResourceHubHeader = styled.div<ResourceHubElementWithColorSchemeProps>`
  position: relative;
  z-index: 2;
  text-align: center;
  width: 100%;
  margin: 0 auto;
  margin-bottom: ${({ theme }) => theme.rem(32)};

  ${({ theme }) => theme.mediaQuery.lg(`
    margin-bottom: ${theme.rem(48)};
    width: 60%;
    max-width: ${theme.rem(640)};
  `)}

  ${Heading} {
    margin-bottom: ${({ theme }) => theme.rem(8)};
    color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].surface.text.base.negative};
  }

  ${Paragraph} {
    max-width: ${({ theme }) => theme.rem(800)};
    margin: 0 auto;
    color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].surface.text.base.negative};
    font-size: ${({ theme }) => theme.rem(40)};
    line-height: ${({ theme }) => theme.rem(48)};

    ${({ theme }) => theme.mediaQuery.lg(`
      font-size: ${theme.rem(60)};
      line-height: ${theme.rem(72)};
    `)}
  }
`

export const ResourceHubLanding = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.rem(16)};
  width: 100%;
  margin: 0 auto;
  padding: ${({ theme }) => `${theme.rem(20)} ${theme.rem(24)}`};
  background: ${({ theme }) => theme.palette.grayscale['50']};
  border-radius: ${({ theme }) => theme.rem(12)};
  box-shadow: 0 2px 12px 0 rgba(0, 34, 33, 0.10);

  ${({ theme }) => theme.mediaQuery.lg(`
    width: max-content;
    max-width: 90%;
  `)}
`

export const ResourceHubSearchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.rem(8)};
  width: 100%;

  ${SearchInputButton} {
    display: none;

    ${({ theme }) => theme.mediaQuery.lg(`
      display: flex;
    `)}
  }
`

export const ResourceHubCategoryLabel = styled.div`
  display: none;
  justify-content: flex-start;
  width: 100%;

  ${({ theme }) => theme.mediaQuery.lg(`
    display: flex;
  `)}

  ${Paragraph} {
    text-align: left;
    color: ${({ theme }) => theme.palette.grayscale['500']};
    margin-bottom: ${({ theme }) => theme.rem(8)};
  }
`

export const ResourceHubCategories = styled.div<ResourceHubElementWithColorSchemeProps>`
  display: none;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.rem(12)};
  width: 100%;

  ${({ theme }) => theme.mediaQuery.lg(`
    display: flex;
  `)}

  ${StyledButton} {
    border-color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].icon.base.tertiary};
    color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].icon.base.tertiary};

    ${Icon} {
      color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].icon.base.tertiary};

      svg {
        width: ${({ theme }) => theme.rem(24)};
        height: ${({ theme }) => theme.rem(24)};
      }
    }

    &:hover {
      color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].icon.base.secondary};
      background-color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].tabs.selected.bgPressed};
      border-color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].tabs.selected.bgPressed};

      ${Icon} {
        color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].icon.base.secondary};
      }
    }
  }
`

export const ResourceHubFiltersBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.rem(16)};
  margin-bottom: ${({ theme }) => theme.rem(32)};
  padding: ${({ theme }) => `${theme.rem(20)} ${theme.rem(24)}`};
  border-radius: ${({ theme }) => theme.rem(12)};
  box-shadow: 0 2px 12px 0 rgba(0, 34, 33, 0.10);
  background: ${({ theme }) => theme.palette.grayscale['50']};
`

export const ResourceHubFiltersBarSearch = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.rem(8)};
  width: 100%;

  ${SearchInputButton} {
    display: none;

    ${({ theme }) => theme.mediaQuery.lg(`
      display: flex;
    `)}
  }
`

export const ResourceHubFiltersBarSelects = styled.div`
  display: none;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.rem(8)};

  ${({ theme }) => theme.mediaQuery.lg(`
    display: flex;
  `)}
`

export const ResourceHubFiltersBarTotalItems = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  ${Paragraph} {
    color: ${({ theme }) => theme.palette.green['300']};
  }
`

export const ClearAllFiltersButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-size: ${({ theme }) => theme.rem(14)};
  color: ${({ theme }) => theme.palette.green['300']};
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    text-decoration: none;
  }
`

export const ResourceHubResultsWrapper = styled.div`
  position: relative;
`

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

export const ResourceHubOverlay = styled.div<{ $visible: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.5);
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  z-index: ${({ $visible }) => ($visible ? 5 : -1)};
  transition: opacity 0.25s ease;
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};

  svg {
    width: ${({ theme }) => theme.rem(40)};
    height: ${({ theme }) => theme.rem(40)};
    animation: ${spin} 0.8s linear infinite;
    color: ${({ theme }) => theme.colors.grey900};
  }
`

export const ResourceHubContent = styled.div`
  width: 100%;
`

export const ResourceHubLoading = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.rem(40)};
  color: ${({ theme }) => theme.colors.grey500};
`

export const ResourceHubError = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.rem(40)};
  color: ${({ theme }) => theme.colors.error || '#dc3545'};
`

export const ResourceHubEmpty = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.rem(40)};
  color: ${({ theme }) => theme.colors.grey500};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.rem(12)};
`

export const ResourceHubGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.rem(16)};

  ${({ theme }) => theme.mediaQuery.md(`
    grid-template-columns: repeat(2, 1fr);
    gap: ${theme.rem(24)};
  `)}

  ${({ theme }) => theme.mediaQuery.lg(`
    grid-template-columns: repeat(3, 1fr);
  `)}
`

export const ResourceHubCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.rem(16)} ${({ theme }) => theme.rem(24)} ;
  border-radius: ${({ theme }) => theme.rem(8)};
  background: ${({ theme }) => theme.palette.grayscale['50']};
  border: 1px solid ${({ theme }) => theme.palette.grayscale['100']};
`

export const ResourceHubCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.rem(16)};
`

export const ResourceHubCardCategory = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.rem(6)};
  color: ${({ theme }) => theme.palette.grayscale['600']};

  span {
    font-size: ${({ theme }) => theme.rem(12)};
    font-weight: 400;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    line-height: ${({ theme }) => theme.rem(16)};
  }

  svg {
    width: ${({ theme }) => theme.rem(20)};
    height: ${({ theme }) => theme.rem(20)};
  }
`

export const ResourceHubCardFileInfo = styled.span`
  font-size: ${({ theme }) => theme.rem(12)};
  font-weight: 400;
  color: ${({ theme }) => theme.palette.grayscale['500']};
`

export const ResourceHubCardTitle = styled.h3`
  margin-bottom: ${({ theme }) => theme.rem(8)};
  font-size: ${({ theme }) => theme.rem(24)};
  font-weight: 400;
  line-height: 1.3;
  color: ${({ theme }) => theme.palette.grayscale['900']};
`

export const ResourceHubCardDescription = styled.div`
  margin-bottom: ${({ theme }) => theme.rem(20)};
  font-size: ${({ theme }) => theme.rem(16)};
  font-weight: 400;
  line-height: 1.5;
  color: ${({ theme }) => theme.palette.grayscale['600']};
  flex: 1;

  p {
    margin: 0;
  }
`

export const ResourceHubCardFooter = styled.div`
  margin-top: auto;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${({ theme }) => theme.rem(8)};
`

export const ResourceHubPagination = styled.nav`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.rem(8)};
  margin-top: ${({ theme }) => theme.rem(32)};
`

export const PaginationButton = styled.button<{ $active?: boolean } & ResourceHubElementWithColorSchemeProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => theme.rem(40)};
  height: ${({ theme }) => theme.rem(40)};
  padding: 0;
  border-radius: ${({ theme }) => theme.rem(8)};
  font-size: ${({ theme }) => theme.rem(16)};
  font-weight: 400;
  line-height: 1;
  cursor: pointer;
  transition: all 0.4s ease;
  
  border: 1px solid transparent;
  background-color: ${({ theme, $active, colorScheme }) =>
    $active ? theme.colorScheme[colorScheme].resourceHub.pagination.bgActive : theme.colorScheme[colorScheme].resourceHub.pagination.bgDefault};
  color: ${({ theme, $active, colorScheme }) =>
    $active ? theme.colorScheme[colorScheme].resourceHub.pagination.textActive : theme.colorScheme[colorScheme].resourceHub.pagination.textDefault};

  &:hover:not(:disabled) {
    background-color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].resourceHub.pagination.bgHover};
    color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].resourceHub.pagination.textHover};
    border-color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].resourceHub.pagination.borderHover};
  }

  &:active {
    background-color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].resourceHub.pagination.bgActive};
    color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].resourceHub.pagination.textActive};
  }

  &:disabled {
    background-color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].resourceHub.pagination.bgDisabled};
    color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].resourceHub.pagination.textDisabled};
    cursor: not-allowed;
  }

  svg {
    width: ${({ theme }) => theme.rem(18)};
    height: ${({ theme }) => theme.rem(18)};
  }
`

export const PaginationDots = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => theme.rem(44)};
  height: ${({ theme }) => theme.rem(44)};
  font-size: ${({ theme }) => theme.rem(16)};
  color: ${({ theme }) => theme.colors.grey500};
  user-select: none;
`

export const FilterIconButton = styled.button<ResourceHubElementWithColorSchemeProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${({ theme }) => theme.rem(42)};
  height: ${({ theme }) => theme.rem(42)};
  border-radius: ${({ theme }) => theme.rem(4)};
  border: 1px solid ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].icon.base.tertiary};
  color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].icon.base.tertiary};
  background: ${({ theme }) => theme.palette.grayscale['50']};
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.palette.grayscale['100']};
  }

  svg {
    width: ${({ theme }) => theme.rem(24)};
    height: ${({ theme }) => theme.rem(24)};
  }

  ${({ theme }) => theme.mediaQuery.lg(`
    display: none;
  `)}
`

export const FiltersModal = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.palette.grayscale['50']};
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
  transition: opacity 0.3s ease;
`

export const FiltersModalTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.rem(24)};
  font-weight: 400;
  line-height: ${({ theme }) => theme.rem(32)};
  color: ${({ theme }) => theme.palette.grayscale['900']};
`

export const FiltersModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => `${theme.rem(16)} ${theme.rem(24)}`};

  ${Heading} {
    font-size: ${({ theme }) => theme.rem(24)};
    line-height: ${({ theme }) => theme.rem(32)};
  }

  svg {
    width: ${({ theme }) => theme.rem(24)};
    height: ${({ theme }) => theme.rem(24)};
  }
`

export const FiltersModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => `0 ${theme.rem(24)}`};
`

export const FiltersModalAccordionPanel = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.palette.grayscale['200']};
`

export const FiltersModalAccordionHeader = styled.button<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${({ theme }) => `${theme.rem(16)} 0`};
  border: none;
  background: none;
  font-size: ${({ theme }) => theme.rem(16)};
  font-weight: 400;
  line-height: ${({ theme }) => theme.rem(24)};
  color: ${({ theme }) => theme.palette.grayscale['900']};
  cursor: pointer;

  > span {
    display: flex;
    align-items: center;
  }

  > ${Icon} {
    flex-shrink: 0;

    svg {
      width: ${({ theme }) => theme.rem(20)};
      height: ${({ theme }) => theme.rem(20)};
      transition: transform 0.2s ease;
      transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0)')};
    }
  }
`

export const FiltersModalAccordionBody = styled.div`
  padding-bottom: ${({ theme }) => theme.rem(16)};
`

export const FiltersModalSortOption = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.rem(10)};
  width: 100%;
  padding: ${({ theme }) => `${theme.rem(10)} 0`};
  border: none;
  background: none;
  font-size: ${({ theme }) => theme.rem(14)};
  color: ${({ theme }) => theme.palette.grayscale['900']};
  cursor: pointer;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};

  &::before {
    content: '';
    display: block;
    width: ${({ theme }) => theme.rem(18)};
    height: ${({ theme }) => theme.rem(18)};
    flex-shrink: 0;
    border-radius: 50%;
    border: 1.5px solid ${({ theme, $active }) =>
      $active ? theme.palette.green['300'] : theme.palette.grayscale['400']};
    background: ${({ theme, $active }) =>
      $active ? theme.palette.green['300'] : 'transparent'};
    box-shadow: ${({ theme, $active }) =>
      $active ? `inset 0 0 0 3px ${theme.palette.grayscale['50']}` : 'none'};
  }
`

export const FiltersModalFooter = styled.div<ResourceHubElementWithColorSchemeProps>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.rem(12)};
  padding: ${({ theme }) => `${theme.rem(16)} ${theme.rem(24)}`};
  border-top: 1px solid ${({ theme }) => theme.palette.grayscale['200']};

  ${StyledButton} {
    flex: 1;

    &:first-of-type {
      border: ${({ theme, colorScheme }) => `1px solid ${theme.colorScheme[colorScheme].button.primary.borderDefault}`};
    }
  }
`
